import React from 'react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import FunwooAPI, {
  BannerSettingEnum,
  HomepageVimeoEntity,
  JobEntity,
  ListingDetail,
  PageOfUserStory,
  StaticOptionCategory,
  StaticOptionEntity,
  UserStoryShowcaseEntity,
} from '@/swagger';
import { GetStaticProps, NextPage } from 'next';

import HomeApp from '@/src/components/UI/Home';
import QRCodeFloatingButton from '@/src/components/feature/QRCodeFloatingButton';
import { BannerHref, resolveBannerHref, ResolvedBannerHref } from '@/src/helper/bannerHref';

interface Props {
  listings: Array<ListingDetail>;
  jobs: Array<JobEntity>;
  conferenceOptions: Array<StaticOptionEntity>;
  userStories: Array<UserStoryShowcaseEntity>;
  banner: StaticOptionEntity;
  bannerHref: ResolvedBannerHref | null;
  vimeo: HomepageVimeoEntity;
}

/**
 * The homepage banner click target. Declared as a { type, sid } spec so it can
 * be re-validated against the API on each ISR revalidation — if the object
 * goes off-shelf, resolveBannerHref returns null and the banner becomes
 * non-clickable.
 */
const HOMEPAGE_BANNER_HREF: BannerHref = { type: 'listing', sid: '109749' };

const Home: NextPage<Props> = (props) => {
  return (
    <React.Fragment>
      <QRCodeFloatingButton />
      <HomeApp {...props} />
    </React.Fragment>
  );
};
export const getStaticProps: GetStaticProps<Props> = async ({ locale = '' }) => {
  const { data: listingData } = await FunwooAPI.listingApi.search({ paging: { page: 1, pageSize: 6 } });
  const { data: jobData } = await FunwooAPI.jobApi.findAll();
  const { data: conferenceOptions = [] } = await FunwooAPI.systemApi.findAllStaticOptionsByCategory({
    category: StaticOptionCategory.CONFERENCE_LOCATION,
  });
  const { data: userStories } = await FunwooAPI.userStory.getShowcases(PageOfUserStory.ABROAD);

  const { data: banner } = await FunwooAPI.systemApi.getBanner({ page: BannerSettingEnum.HOMEPAGE });
  const { data: vimeo } = await FunwooAPI.homepageApi.getVimeo();

  const bannerHref = await resolveBannerHref(HOMEPAGE_BANNER_HREF);

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      listings: listingData.payload ?? [],
      jobs: jobData,
      conferenceOptions,
      userStories,
      banner,
      bannerHref,
      vimeo,
    },
    revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_INTERVAL),
  };
};
export default Home;
